import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { API_URL } from "../utils/utils";
import Swal from "sweetalert2";

// Helper function to initialize feather icons
const initFeatherIcons = () => {
  if (window.feather) {
    window.feather.replace();
  } else {
    setTimeout(initFeatherIcons, 100);
  }
};

export default function BookingDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const booking = location.state?.booking;
  const editModeFromList = location.state?.editMode || false;
  const EDIT_WINDOW_HOURS = 24;

  const isWithinEditWindow = (() => {
    if (!booking || !booking.created_at) return false;
    const createdMs = new Date(booking.created_at).getTime();
    if (Number.isNaN(createdMs)) return false;
    const now = Date.now();
    const elapsedMs = now - createdMs;
    const windowMs = EDIT_WINDOW_HOURS * 60 * 60 * 1000;
    return elapsedMs <= windowMs;
  })();

  const canEdit = isWithinEditWindow;

  const toInputDateTime = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [isEditing, setIsEditing] = useState(editModeFromList && canEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [editData, setEditData] = useState(() => ({
    pickupDateTimeInput: toInputDateTime(booking?.pickup_date_time),
    returnDateTimeInput: toInputDateTime(booking?.return_date_time),
    totalPassengers: booking?.total_passengers || 0,
    smallLuggage: booking?.small_luggage || 0,
    mediumLuggage: booking?.medium_luggage || 0,
    largeLuggage: booking?.large_luggage || 0,
    status: booking?.status || "",
  }));

  useEffect(() => {
    const timer = setTimeout(() => {
      initFeatherIcons();
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  if (!booking) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center py-5">
              <h5 className="text-muted">Booking not found</h5>
              <button
                className="btn btn-primary mt-3"
                onClick={() => navigate("/dashboard/bookings")}
              >
                Back to Bookings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return `£${parseFloat(amount).toFixed(2)}`;
  };

  const getPaymentMethodLabel = (method) => {
    const key = (method || "").toLowerCase();
    switch (key) {
      case "visa_card":
      case "visa":
        return "Visa Card";
      case "master_card":
      case "mastercard":
        return "Master Card";
      case "google_pay":
      case "gpay":
        return "Google Pay";
      case "apple_pay":
      case "applepay":
        return "Apple Pay";
      default:
        return method || "N/A";
    }
  };

  const handleEditFieldChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const editableFieldStyle = {
    borderColor: "#d70015",
    boxShadow: "0 0 0 0.2rem rgba(215, 0, 21, 0.15)",
  };

  const toISOIfValid = (dateTimeLocal) => {
    if (!dateTimeLocal) return null;
    const d = new Date(dateTimeLocal);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  };

  const handleSaveChanges = async () => {
    try {
      setIsSaving(true);
      const payload = {
        id: booking.id,
        booking_no: booking.booking_no,
        pickup_date_time: toISOIfValid(editData.pickupDateTimeInput) || editData.pickupDateTimeInput,
        return_date_time: toISOIfValid(editData.returnDateTimeInput) || editData.returnDateTimeInput,
        total_passengers: Number(editData.totalPassengers) || 0,
        small_luggage: Number(editData.smallLuggage) || 0,
        medium_luggage: Number(editData.mediumLuggage) || 0,
        large_luggage: Number(editData.largeLuggage) || 0,
        status: editData.status || booking.status,
      };

      const bodyString = JSON.stringify(payload);
      const updateUrl = `${API_URL}bookings/update/${booking.id}`;
      const response = await fetch(updateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: bodyString,
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error("Update response not ok:", response.status, errText);
        throw new Error(errText || `Update failed with status ${response.status}`);
      }

      await Swal.fire({
        icon: "success",
        title: "Booking Updated",
        text: "Your booking has been updated successfully.",
        confirmButtonText: "OK",
      });

      navigate("/dashboard/bookings");
    } catch (error) {
      console.error("Error updating booking:", error);
      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: "We could not update your booking. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelBooking = async () => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Cancel Booking?",
      text: "Are you sure you want to cancel this booking?",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel it",
      cancelButtonText: "No",
      confirmButtonColor: "#d70015",
    });

    if (!result.isConfirmed) return;

    try {
      setIsSaving(true);

      const payload = {
        id: booking.id,
        booking_no: booking.booking_no,
        status: "cancelled",
      };

      const bodyString = JSON.stringify(payload);
      const updateUrl = booking.id != null
        ? `${API_URL}bookings/update/${booking.id}`
        : `${API_URL}bookings/update`;
      const response = await fetch(updateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: bodyString,
      });

      if (!response.ok) {
        const errText = await response.text();
        console.error("Cancel response not ok:", response.status, errText);
        throw new Error(errText || `Cancel failed with status ${response.status}`);
      }

      await Swal.fire({
        icon: "success",
        title: "Booking Cancelled",
        text: "Your booking has been cancelled.",
        confirmButtonText: "OK",
      });

      navigate("/dashboard/bookings");
    } catch (error) {
      console.error("Error cancelling booking:", error);
      await Swal.fire({
        icon: "error",
        title: "Cancel Failed",
        text: "We could not cancel your booking. Please try again.",
        confirmButtonText: "OK",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || "").toLowerCase();
    let badgeClass = "badge bg-secondary";
    
    if (statusLower.includes("confirmed") || statusLower.includes("completed")) {
      badgeClass = "badge bg-success";
    } else if (statusLower.includes("pending") || statusLower.includes("processing")) {
      badgeClass = "badge bg-warning";
    } else if (statusLower.includes("cancelled") || statusLower.includes("canceled")) {
      badgeClass = "badge bg-danger";
    }
    
    return <span className={badgeClass}>{status || "N/A"}</span>;
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center flex-wrap grid-margin">
        <div>
          <h4 className="mb-3 mb-md-0" style={{ color: "#d70015" }}>Booking Details</h4>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/dashboard/bookings")}
        >
          <i className="link-icon" data-feather="arrow-left" /> Back to Bookings
        </button>
      </div>
      <div className="row">
        <div className="col-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-4">
                  <h5 className="mb-3" style={{ color: "#d70015" }}>Booking Information</h5>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td className="fw-bold">Booking Number:</td>
                        <td>{booking.booking_no || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Trip Type:</td>
                        <td>{booking.trip_type || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Status:</td>
                        <td>{getStatusBadge(booking.status)}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Total Passengers:</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm"
                              style={editableFieldStyle}
                              value={editData.totalPassengers}
                              onChange={(e) =>
                                handleEditFieldChange("totalPassengers", e.target.value)
                              }
                            />
                          ) : (
                            booking.total_passengers || "N/A"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Distance:</td>
                        <td>{booking.distance ? `${booking.distance} miles` : "N/A"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6 mb-4">
                  <h5 className="mb-3" style={{ color: "#d70015" }}>Passenger Information</h5>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td className="fw-bold">First Name:</td>
                        <td>{booking.passenger_first_name || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Last Name:</td>
                        <td>{booking.passenger_last_name || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Email:</td>
                        <td>{booking.passenger_email || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Contact No:</td>
                        <td>{booking.passenger_contact_no || "N/A"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-4">
                  <h5 className="mb-3" style={{ color: "#d70015" }}>Pickup Information</h5>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td className="fw-bold">Location:</td>
                        <td>{booking.pickup_location || "N/A"}</td>
                      </tr>
                      
                      <tr>
                        <td className="fw-bold">Pickup Date/Time:</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="datetime-local"
                              className="form-control form-control-sm"
                              style={editableFieldStyle}
                              value={editData.pickupDateTimeInput}
                              onChange={(e) =>
                                handleEditFieldChange("pickupDateTimeInput", e.target.value)
                              }
                            />
                          ) : (
                            formatDate(booking.pickup_date_time)
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Flight Date/Time:</td>
                        <td>{formatDate(booking.flight_date_time)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6 mb-4">
                  <h5 className="mb-3" style={{ color: "#d70015" }}>Drop-off Information</h5>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td className="fw-bold">Location:</td>
                        <td>{booking.dropoff_location || "N/A"}</td>
                      </tr>

                      <tr>
                        <td className="fw-bold">Return Date/Time:</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="datetime-local"
                              className="form-control form-control-sm"
                              style={editableFieldStyle}
                              value={editData.returnDateTimeInput}
                              onChange={(e) =>
                                handleEditFieldChange("returnDateTimeInput", e.target.value)
                              }
                            />
                          ) : (
                            formatDate(booking.return_date_time)
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Return Flight Date/Time:</td>
                        <td>{formatDate(booking.return_flight_date_time)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-4">
                  <h5 className="mb-3" style={{ color: "#d70015" }}>Luggage Information</h5>
                  <table className="table table-borderless">
                    <tbody>

                    
                      <tr>
                        <td className="fw-bold">Medium Luggage:</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm"
                              style={editableFieldStyle}
                              value={editData.mediumLuggage}
                              onChange={(e) =>
                                handleEditFieldChange("mediumLuggage", e.target.value)
                              }
                            />
                          ) : (
                            booking.medium_luggage || 0
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Large Luggage:</td>
                        <td>
                          {isEditing ? (
                            <input
                              type="number"
                              min="0"
                              className="form-control form-control-sm"
                              style={editableFieldStyle}
                              value={editData.largeLuggage}
                              onChange={(e) =>
                                handleEditFieldChange("largeLuggage", e.target.value)
                              }
                            />
                          ) : (
                            booking.large_luggage || 0
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Child Seat:</td>
                        <td>
                          {booking.car_seat ?? booking.child_seat ?? 0}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6 mb-4">
                  <h5 className="mb-3" style={{ color: "#d70015" }}>Payment Information</h5>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td className="fw-bold">Mode of Payment:</td>
                        <td>
                          {getPaymentMethodLabel(
                            booking.payment_method || booking.mode_of_payment
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Payment Reference:</td>
                        <td>{booking.payment_reference_no || "N/A"}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Paid Amount:</td>
                        <td>{formatCurrency(booking.paid_amount)}</td>
                      </tr>
                      <tr>
                        <td className="fw-bold">Payment Date/Time:</td>
                        <td>{formatDate(booking.payment_datetime)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-4">
                  <h5 className="mb-3" style={{ color: "#d70015" }}>Charges</h5>
                  <table className="table table-borderless">
                    <tbody>
                      <tr>
                        <td className="fw-bold">Total Charges:</td>
                        <td className="fs-5 text-primary">
                          {formatCurrency(booking.total_charges)}
                        </td>
                      </tr>

                    </tbody>
                  </table>
                </div>
                {canEdit && (
                  <div className="col-md-6 mb-4 d-flex align-items-end justify-content-end">
                    {!isEditing ? (
                      <button
                        type="button"
                        className="btn btn-outline-secondary me-2"
                        style={{ borderColor: "#d70015", color: "#d70015" }}
                        onClick={() => setIsEditing(true)}
                      >
                        <i className="fa fa-pencil me-1" /> Edit Booking
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-primary me-2"
                          style={{ backgroundColor: "#d70015", borderColor: "#d70015" }}
                          disabled={isSaving}
                          onClick={handleSaveChanges}
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary me-2"
                          disabled={isSaving}
                          onClick={() => navigate("/dashboard/bookings")}
                        >
                          Cancel Edit
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={isSaving}
                      onClick={handleCancelBooking}
                    >
                      Cancel Booking
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
